/**
 * Operators panel — floating panel + F3 search palette.
 *
 * Follows Blender's operator model:
 * - Floating panel shows relevant operators for the current context,
 *   with favorites surfaced at the top.
 * - "Search All…" opens a search palette (like Blender's F3) listing
 *   every operator with search filtering and one-tap favorite toggling.
 *
 * Designed for touch parity — every interaction is tap-friendly, no
 * hover-only affordances.
 */

import { useCallback, useMemo } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { useStore } from "../app/store.ts";
import { useOperatorContext } from "../app/useOperatorContext.ts";
import { isOperatorActiveFromCommand } from "../modeling/operatorParams.ts";
import {
  OPERATOR_CATEGORY_LABELS,
  OPERATORS,
  type Operator,
  type OperatorContext,
  partitionOperators,
} from "../modeling/operators.ts";
import { Button } from "./Button.tsx";
import { StarFilledIcon, StarIcon } from "./Icons.tsx";
import { SearchPalette, type SearchPaletteItem } from "./SearchPalette.tsx";
import { Kbd } from "./shared.tsx";
import {
  COLOR_BORDER,
  COLOR_CAUTION,
  COLOR_CAUTION_BG,
  COLOR_GOLD,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Operators panel — rendered inside a floating panel
// ---------------------------------------------------------------------------

export function OperatorsPanel() {
  const favoriteIds = useStore((s) => s.favoriteOperators);
  const setOperatorSearchOpen = useStore((s) => s.setOperatorSearchOpen);
  const ctx = useOperatorContext();

  const { favorites, others } = useMemo(
    () => partitionOperators(ctx, favoriteIds),
    [ctx, favoriteIds],
  );

  const hasFavorites = favorites.length > 0;
  const hasOthers = others.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Favorites first */}
      {hasFavorites && (
        <>
          {favorites.map((op) => (
            <OperatorRow key={op.id} operator={op} ctx={ctx} />
          ))}
          {hasOthers && (
            <div
              style={{
                borderTop: `0.5px solid ${COLOR_BORDER}`,
                margin: "4px 0",
              }}
            />
          )}
        </>
      )}

      {/* Non-favorites */}
      {others.map((op) => (
        <OperatorRow key={op.id} operator={op} ctx={ctx} />
      ))}

      {/* Empty state */}
      {!hasFavorites && !hasOthers && (
        <div
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_FAINT,
            padding: "4px 0",
          }}
        >
          No operators for this context
        </div>
      )}

      {/* Search all trigger */}
      <Button
        onClick={() => setOperatorSearchOpen(true)}
        variant="secondary"
        size="xs"
        fullWidth
        style={{
          marginTop: 6,
          background: "transparent",
          color: COLOR_TEXT_FAINT,
        }}
      >
        Search All…
        <Kbd shortcut={getShortcutLabel("toggleOperatorSearch") ?? "F3"} />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OperatorRow — single operator row used in both panel and search palette
// ---------------------------------------------------------------------------

function OperatorRow({
  operator,
  ctx,
  showCategory,
}: {
  operator: Operator;
  ctx: OperatorContext;
  showCategory?: boolean;
}) {
  const toggleFavorite = useStore((s) => s.toggleFavoriteOperator);
  const isFavorite = useStore((s) => s.favoriteOperators.has(operator.id));
  const isActive = useStore((s) => {
    if (!operator.toggle) return false;
    if (s.activeOperator === operator.id) return true;
    // Modal operators that use the command buffer instead of activeOperator
    const cb = s.commandBuffer;
    return cb ? isOperatorActiveFromCommand(operator.id, cb.type) : false;
  });
  const enabled = operator.isEnabled(ctx);
  const Icon = operator.icon;
  const shortcut = getShortcutLabel(operator.id);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "5px 8px",
        borderRadius: RADIUS_CONTROL,
        background: isActive ? COLOR_CAUTION_BG : "transparent",
        opacity: enabled ? 1 : 0.45,
        cursor: enabled ? "pointer" : "default",
      }}
      onClick={enabled ? () => operator.execute() : undefined}
      title={shortcut ? `${operator.label} (${shortcut})` : operator.label}
    >
      {/* Favorite star */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(operator.id);
        }}
        style={{
          cursor: "pointer",
          flexShrink: 0,
          color: isFavorite ? COLOR_GOLD : COLOR_TEXT_DISABLED,
          display: "flex",
          alignItems: "center",
          opacity: enabled ? 1 : 1, // star always tappable
        }}
      >
        {isFavorite ? <StarFilledIcon /> : <StarIcon />}
      </span>

      {/* Icon */}
      <span
        style={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          color: isActive
            ? COLOR_CAUTION
            : enabled
              ? COLOR_TEXT_SUBTLE
              : COLOR_TEXT_DISABLED,
        }}
      >
        <Icon size={13} />
      </span>

      {/* Label */}
      <span
        style={{
          flex: 1,
          fontSize: FONT_SIZE_SMALL,
          color: isActive
            ? COLOR_CAUTION
            : enabled
              ? COLOR_TEXT_SECONDARY
              : COLOR_TEXT_DISABLED,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {operator.label}
        {showCategory && (
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              marginLeft: 6,
            }}
          >
            {OPERATOR_CATEGORY_LABELS[operator.category]}
          </span>
        )}
      </span>

      {/* Shortcut chip */}
      {shortcut && <Kbd shortcut={shortcut} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Floating operator search palette (F3) — thin wrapper over SearchPalette
// ---------------------------------------------------------------------------

/** Star-toggle prefix for each operator row in the search palette. */
function FavoriteStar({ operatorId }: { operatorId: string }) {
  const toggleFavorite = useStore((s) => s.toggleFavoriteOperator);
  const isFavorite = useStore((s) => s.favoriteOperators.has(operatorId));
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        toggleFavorite(operatorId);
      }}
      style={{
        cursor: "pointer",
        flexShrink: 0,
        color: isFavorite ? COLOR_GOLD : COLOR_TEXT_DISABLED,
        display: "flex",
        alignItems: "center",
      }}
    >
      {isFavorite ? <StarFilledIcon /> : <StarIcon />}
    </span>
  );
}

export function OperatorSearchPanel() {
  const open = useStore((s) => s.operatorSearchOpen);
  const setOpen = useStore((s) => s.setOperatorSearchOpen);
  const favoriteIds = useStore((s) => s.favoriteOperators);
  const ctx = useOperatorContext();

  // Map Operators → SearchPaletteItems, grouped by category, favorites first
  const items = useMemo<SearchPaletteItem[]>(() => {
    // Sort: favorites float to top within each category
    const sorted = [...OPERATORS].sort((a, b) => {
      // Primary: group by category
      if (a.category !== b.category) return a.category < b.category ? -1 : 1;
      // Secondary: favorites first
      const af = favoriteIds.has(a.id) ? 0 : 1;
      const bf = favoriteIds.has(b.id) ? 0 : 1;
      return af - bf;
    });

    return sorted.map((op) => ({
      id: op.id,
      label: op.label,
      icon: <op.icon size={13} />,
      shortcut: getShortcutLabel(op.id),
      group: OPERATOR_CATEGORY_LABELS[op.category] ?? op.category,
      disabled: !op.isEnabled(ctx),
      prefix: <FavoriteStar operatorId={op.id} />,
    }));
  }, [favoriteIds, ctx]);

  const handleSelect = useCallback(
    (id: string) => {
      const op = OPERATORS.find((o) => o.id === id);
      if (op?.isEnabled(ctx)) {
        op.execute();
        setOpen(false);
      }
    },
    [ctx, setOpen],
  );

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  return (
    <SearchPalette
      open={open}
      onClose={handleClose}
      title="Operator Search"
      placeholder="Search operators…"
      items={items}
      emptyLabel="No matching operators"
      onSelect={handleSelect}
    />
  );
}
